import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { DELETE_PURCHASE, DELETE_PURCHASE_RETURN, EDIT_PURCHASE } from "../../constants";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { OfficePurchaseTableColumns } from "../../pages/office/constants";
import { PurchaseService } from "../../services/purchase.service";
import { derivePdfColumns, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

const DEFAULT_SORT = [{ field: "date", sort: "desc" as const }];

export const OfficePurchase = () => {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "office-purchases",
    defaults: { sortModel: DEFAULT_SORT },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null, type: null });

  const hasEditPurchase = useHasPermission(EDIT_PURCHASE);
  const hasDeletePurchase = useHasPermission(DELETE_PURCHASE);
  const canDeletePurchaseReturn = useHasPermission(DELETE_PURCHASE_RETURN);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(async (purchaseFilters = {}) => {
    setLoading(true);

    try {
      const { fromDate, toDate, sortBy, ...rest } = purchaseFilters as any;
      let mappedSortBy = sortBy;
      if (sortBy === "date") {
        mappedSortBy = "receivedDate";
      }

      const purchaseResponse = await PurchaseService.getPurchases({
        ...rest,
        receivedDateFrom: fromDate,
        receivedDateTo: toDate,
        sortBy: mappedSortBy,
        isOffice: true,
      });
      const vendorIds = Array.from(new Set((purchaseResponse?.data?.rows || []).map((item) => item.vendorId)));
      let vendors = [];
      if (vendorIds.length > 0) {
        const vendorResponse = await PurchaseService.getVendors({
          ids: vendorIds.join(","),
          limit: vendorIds.length,
          includeDeleted: true,
        });
        vendors = vendorResponse?.data?.rows || [];
      }

      const mappedInvoices = (purchaseResponse?.data?.rows || []).map((statement, index) => {
        const vendor = vendors.find((v) => v.id === statement.vendorId);
        return {
          sl: index + 1 + ((purchaseFilters.page || 1) - 1) * (purchaseFilters.limit || 10),
          id: statement.id,
          date: moment(statement.receivedDate || statement.date).format("DD MMM YYYY"),
          vendorName: vendor?.name || "-",
          amount: statement.totalInvoiceAmount || statement.amount || 0,
          vendorId: statement.vendorId,
          type: "Purchase",
        };
      });

      setInvoices(mappedInvoices);
      setTotalCount(purchaseResponse?.data?.count || 0);
    } catch (error) {
      toast.error("Failed to fetch office purchase statement");
      console.error("OfficePurchase ~ fetchData error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dateRange) return;

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      description: searchText,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [fetchData, sortModel, paginationState, searchText, dateRange]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(OfficePurchaseTableColumns());
      const printableRows = invoices.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [key, value === undefined || value === null ? "" : String(value)]),
        ),
      );

      const pdfDocument = (
        <Listing title="Office Purchases" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Office Purchases",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params) => {
    const { vendorId, id, type } = params.row;
    // TODO: Wire office purchase detail routes when backend support is available.
    if (type === "Purchase") {
      navigate(`/vendor/${vendorId}/invoice/${id}`);
    } else {
      navigate(`/vendor/${vendorId}/return/${id}`);
    }
  };

  const openDeleteModal = (rowData) => {
    setDeleteModal({ isOpen: true, recordId: rowData.id, type: rowData.type });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, recordId: null, type: null });
  };

  const handleDelete = async () => {
    try {
      if (deleteModal.type === "Return") {
        await PurchaseService.deletePurchaseReturn(deleteModal.recordId);
      } else {
        await PurchaseService.deletePurchase(deleteModal.recordId);
      }
      toast.success("Record deleted successfully");
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        description: searchText,
        fromDate: dateRange?.startDate,
        toDate: dateRange?.endDate,
      });
    } catch (error) {
      toast.error("Failed to delete record");
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = (rowData, actionType) => {
    switch (actionType) {
      case "edit":
        if (rowData.type === "Purchase") {
          navigate(`/vendor/purchase/edit/${rowData.id}?vendorId=${rowData.vendorId}&accountType=office&tab=purchases`);
        }
        break;
      case "delete":
        openDeleteModal(rowData);
        break;
      default:
        return null;
    }
  };

  const columnHeaders = OfficePurchaseTableColumns(handleActions, hasEditPurchase, hasDeletePurchase || canDeletePurchaseReturn);

  return (
    <CardLayout>
      <MTable
        placeholder="Search Purchase"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showDateRangePicker
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        showExportButton
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        columnHeaders={columnHeaders}
        tableRows={invoices}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        defaultSortModel={DEFAULT_SORT}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message={`Are you sure you want to delete this ${deleteModal.type === "Return" ? "return" : "purchase"}?`}
      />
    </CardLayout>
  );
};
